import { FC, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Input, Row, Typography } from 'antd';
import { TbBrowserCheck, TbPlus } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import {
    EmptySection,
    VirtualizedList as List,
    OneTimeCredentialModal,
    OurDocsLink,
    PlanTag,
    SkeletonList,
    type OneTimeCredentialDisclosure,
} from '@/shared';
import CreateOAuthApplicationModal from '@/shared/components/modals/ResourceCreationModals/CreateOAuthApplicationModal';

import OAuthApplicationListItem, { OAuthApplicationListRecord } from './OAuthApplicationListItem';

export interface OAuthApplication {
    clientSecret?: string;
    installationUrl?: string;
    id: string;
    name: string;
    clientId: string;
    shortDescription?: string;
    status?: string;
    createdAt?: string;
}

const OAuthApplications: FC = (): JSX.Element => {
    const [loading, setLoading] = useState(true);

    const [open, setOpen] = useState(false);

    const [oauthApplications, setOAuthApplications] = useState<OAuthApplication[]>([]);
    const [query, setQuery] = useState('');
    const [credentialDisclosure, setCredentialDisclosure] = useState<OneTimeCredentialDisclosure>();
    const filteredOAuthApplications = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        if (!normalizedQuery) {
            return oauthApplications;
        }

        return oauthApplications.filter(({ name, clientId, shortDescription }) =>
            [name, clientId, shortDescription].some((value) => value?.toLowerCase().includes(normalizedQuery)),
        );
    }, [oauthApplications, query]);

    useEffect(() => {
        let active = true;

        authorizedAxiosInstance
            .get<OAuthApplication[]>('/api/oauth-applications')
            .then(({ data }) => {
                if (active) {
                    setOAuthApplications(data);
                }
            })
            .catch(captureException)
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, []);

    const { hasFeature } = useEntitlements();
    const canUseOAuthApplications = hasFeature('apiAndWebhooks');

    const { token } = useAntDesignTheme();

    return (
        <>
            <Row align='top' wrap={false}>
                <Col flex='auto'>
                    <Typography.Title level={5}>
                        <TbBrowserCheck size='1rem' style={{ verticalAlign: '-0.15rem', marginRight: '0.5rem' }} />
                        OAuth Applications
                        <PlanTag plan='enterprise' style={{ marginLeft: '0.5rem' }} />
                    </Typography.Title>
                    <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                        Create private or public <OurDocsLink text={'OAuth 2.0'} href={'https://oauth.net/2/'} />{' '}
                        integrations for yourself & others. Published integrations will be made available in our{' '}
                        <OurDocsLink
                            text={'Integrations Directory'}
                            href={'https://docs.countercyclical.io/guides/integrations'}
                        />
                    </Typography.Paragraph>
                </Col>
            </Row>
            <Row gutter={[16, 0]}>
                <Col flex='auto'>
                    <Input
                        allowClear
                        value={query}
                        placeholder='Search OAuth Applications...'
                        onChange={(event) => setQuery(event.target.value)}
                    />
                </Col>
                <Col flex='none'>
                    <Button onClick={() => setOpen(true)} type='dashed' disabled={!canUseOAuthApplications}>
                        <TbPlus size='1rem' style={{ verticalAlign: '-0.15rem' }} />
                        Add OAuth Application
                    </Button>
                </Col>
            </Row>
            <CreateOAuthApplicationModal
                open={open}
                setOpen={setOpen}
                onCreated={(oauthApplication) => {
                    const { clientSecret, ...persistedApplication } = oauthApplication;

                    setOAuthApplications((items) => [...items, persistedApplication]);
                    if (clientSecret) {
                        setCredentialDisclosure({
                            copyLabel: 'Copy Client Secret',
                            description: `This is the only time Countercyclical will display the complete client secret for ${oauthApplication.name}. Copy it before closing this modal.`,
                            title: 'OAuth Application Created',
                            values: [{ label: 'Client Secret', value: clientSecret }],
                        });
                    }
                }}
            />
            <OneTimeCredentialModal
                disclosure={credentialDisclosure}
                onClose={() => setCredentialDisclosure(undefined)}
            />
            {filteredOAuthApplications.length !== 0 && !loading ? (
                <>
                    <List
                        dataSource={filteredOAuthApplications}
                        renderItem={(oauthApplication: OAuthApplicationListRecord) => {
                            return (
                                <OAuthApplicationListItem
                                    key={oauthApplication?.id}
                                    item={oauthApplication}
                                    onDeleted={(oauthApplicationId) =>
                                        setOAuthApplications((items) =>
                                            items.filter((item: OAuthApplication) => item?.id !== oauthApplicationId),
                                        )
                                    }
                                    onUpdated={(updatedOAuthApplication) =>
                                        setOAuthApplications((items) =>
                                            items.map((item) =>
                                                item.id === updatedOAuthApplication.id ? updatedOAuthApplication : item,
                                            ),
                                        )
                                    }
                                />
                            );
                        }}
                    />
                </>
            ) : loading ? (
                <SkeletonList listLength={8} maxHeight='20rem' minHeight='20rem' />
            ) : (
                <EmptySection
                    height='25rem'
                    icon={<TbBrowserCheck style={{ color: token.colorTextTertiary }} />}
                    label={query ? 'No Matching OAuth Applications' : 'No OAuth Applications'}
                    description={
                        query
                            ? 'No OAuth applications match the current search.'
                            : 'Add an OAuth application to create a public or private integration between Countercyclical and your platform.'
                    }
                />
            )}
        </>
    );
};

export default OAuthApplications;
